import { memo, useMemo } from "react"
import { cn } from "@/shared/utils/general/cn"
import type { DbObject } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ContainerLabelProps {
  task: DbObject
  className?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract the parent container ID from a parentPath.
 * parentPath format: "/rootId/projectId" → last segment = projectId
 */
function getParentId(parentPath: string): string | null {
  if (!parentPath) return null
  const segments = parentPath.split("/").filter(Boolean)
  return segments.length > 0 ? (segments[segments.length - 1] ?? null) : null
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Shows the parent project/list name below the task title.
 *
 * v1 pattern: CardPreviewContainer — 12px, 40% opacity text.
 * Only visible in views where viewConfig.showContainerLabel is true
 * (Logbook, Upcoming, Trash, Search, TodayLater evening tasks).
 */
export const ContainerLabel = memo(function ContainerLabel({
  task,
  className,
}: ContainerLabelProps) {
  const parentId = useMemo(
    () => getParentId(task.parentPath),
    [task.parentPath],
  )

  const [parent] = useLiveQueryImmediate(
    () => (parentId ? db.totodo.get(parentId) : undefined),
    ["container-label", parentId],
    undefined,
  )

  if (!parent?.title) return null

  return (
    <span
      className={cn(
        "block truncate text-[11px] leading-tight text-foreground/40",
        className,
      )}
    >
      {parent.title}
    </span>
  )
})
